import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  CurrencyInfo,
  CurrencyService,
} from '../../_services/currencies/currency.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { PusherService } from 'src/app/Shared/services/pusher/pusher.service';
import { toZonedTime, format } from 'date-fns-tz';
import { LocalDateTimePipe } from 'src/app/Shared/_pipes/local-date-time';
import { CustomCurrencyPipe } from 'src/app/Shared/_pipes/currency-pipe/custom-currency-pipe.pipe';
import { CurrencySymbolPipe } from 'src/app/Shared/_pipes/currency-symbol/currency-symbol.pipe';

interface CurrencyDisplay extends CurrencyInfo {
  rate: string;
  amount: number;
  to_currency_code: string;
  from_currency_code: string;
}
interface Rate {
  currency_code: string;
  rate: string;
  to_currency_id: number;
  from_currency_id: number;
  rate_at: string;
}

interface CurrencyRate {
  to_currency_code: string;
  from_currency_code: string;
  rate: string;
  currency_id: number;
  rate_at: string;
}

@Component({
    selector: 'app-currency-conversions',
        standalone: true,
imports: [
    CommonModule,
    LocalDateTimePipe,
    CustomCurrencyPipe,
    CurrencySymbolPipe,
    TranslatePipe
],
    templateUrl: './currency-conversions.component.html',
    styleUrl: './currency-conversions.component.scss',
    providers: [CustomCurrencyPipe, CurrencySymbolPipe]
})
export class CurrencyConversionsComponent {
  @Output() close = new EventEmitter<void>();
  @Input() isOpen = false;
  @Input() auctionType = '';
  @Input() locale = '';
  @Input() currency: any;
  @Input() set currencyRates(data: { rates: CurrencyRate[]; rate_at: any }) {
    if (data && data.rates) {
      this._initialRates = data.rates;
      this.updateLocalRates(data.rates);
      this.approximationDate = data?.rate_at;
    }
  }
  @Input() convertingValue$!: Observable<any>;
  currencyArray: CurrencyDisplay[] = [];
  ratesSubscription: any;
  baseCurrency = {
    amount: '',
  };
  private _initialRates: CurrencyRate[] = [];
  private _currentRates: CurrencyRate[] = [];
  approximationDate!: string;
  private subscriptions = new Subscription();

  constructor(
    private currencyService: CurrencyService,
    private pusherService: PusherService
  ) {}
  private updateLocalRates(rates: CurrencyRate[]) {
    if (Array.isArray(rates)) {
      this._currentRates = [...rates];
      this.updateCurrencyArray();
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.convertingValue$.subscribe((value) => {
        this.baseCurrency.amount = value;
        this.updateCurrencyArray();
      })
    );
    if (this.auctionType !== 'previous') {
      this.subscribeToCurrencyRates();
    }
  }

  closeCurrencyPanel() {
    this.close.emit();
  }
  private calculateConvertedAmount(
    rate: string,
    baseAmount: string | number
  ): number {
    const numericRate = parseFloat(rate);
    const numericAmount =
      typeof baseAmount === 'string' ? parseFloat(baseAmount) : baseAmount;

    if (isNaN(numericRate) || isNaN(numericAmount)) {
      return 0;
    }

    // Calculate converted amount
    return numericRate * numericAmount;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currencyRates']) {
      this.updateCurrencyArray();
    }
  }

  private updateCurrencyArray(): void {
    if (!Array.isArray(this._currentRates)) {
      console.warn('Currency rates is not an array');
      return;
    }

    this.currencyArray = this._currentRates
      .map((rateInfo) => {
        const currencyInfo = this.currencyService.getCurrencyInfo(
          rateInfo.to_currency_code
        );
        if (!currencyInfo) return null;

        const convertedAmount = this.calculateConvertedAmount(
          rateInfo.rate,
          this.baseCurrency.amount
        );

        return {
          ...currencyInfo,
          rate: rateInfo.rate,
          amount: convertedAmount,
          to_currency_code: rateInfo.to_currency_code,
          from_currency_code: rateInfo.from_currency_code,
        };
      })
      .filter((currency): currency is CurrencyDisplay => currency !== null);
  }
  subscribeToCurrencyRates() {
    this.ratesSubscription = this.pusherService.subscribe(
      'currency-rates',
      'currency-rates.updated',
      true,
      'Currency rates',
      (data: { rates: CurrencyRate[]; current_datetime: any }) => {
        if (data && data.rates) {
          this.updateLocalRates(data.rates);
          this.approximationDate = data?.current_datetime;
        }
      }
    );
  }

  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  formatTime(date: Date): string {
    return date.toTimeString().split(' ')[0];
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    if (this.ratesSubscription) {
      this.pusherService.unbindEvent(
        'currency-rates',
        'currency-rates.updated'
      );
      this.ratesSubscription = null;
    }
  }
}
